'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { WEBAPP_URL } from '@/constants/http-constants';
import { ID_APOLLO_KINO, ID_INNENSTADT_KINOS } from '@cineamo/api/constants/cinema-ids';

import { isSalesDemoHost } from '@/helpers/public-host';
import { addUtmParams } from '@/helpers/utm-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { getOs } from '@cineamo/ui/src/helpers/os-helper';

import { IconAppStore24 } from '@cineamo/ui/src/icons/ic-appStore';
import { IconGooglePlayColored24 } from '@cineamo/ui/src/icons/ic-googlePlayColored';
import { AriaHiddenEmoji } from '@cineamo/ui/src/misc/AriaHiddenEmoji';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { usePageName } from '@/utils/tracking-helper';

export default function CineamoAppBanner(): React.JSX.Element | null {
    const t = useTranslations();
    const { track } = useAnalytics();
    const domainConfiguration = useDomainConfiguration();
    const currentPageName = usePageName();
    const deviceOs = getOs();

    const cineamoAppBanner = domainConfiguration.websiteConfiguration?.cineamoAppBanner;

    function getBannerText() {
        switch (domainConfiguration.cinema.id) {
            case ID_INNENSTADT_KINOS:
                return cineamoAppBanner?.text || t('common.banner-cineamo-app-innstadtkinos');
            case ID_APOLLO_KINO:
                return cineamoAppBanner?.text || t('common.banner-cineamo-app-apollo-kino');
            default:
                return cineamoAppBanner?.text || t('common.banner-cineamo-app');
        }
    }

    const bannerText = getBannerText();

    // On demo sites the banner reads as Cineamo advertising itself, not as the cinema's own recommendation.
    if (
        cineamoAppBanner?.isEnabled === false ||
        domainConfiguration.isDemoDomain ||
        isSalesDemoHost(domainConfiguration.domain)
    ) {
        return null;
    }

    return (
        <a
            id="__cineamo_app_banner__"
            className={cn(
                'sticky top-0 lg:hidden z-50',
                'flex flex-row items-center justify-center min-h-48 text-body-bold-10 bg-black text-white space-x-12 px-16 py-6'
            )}
            href={addUtmParams(`${WEBAPP_URL}/download-app`, {
                utmSource: 'cinema_website',
                utmMedium: 'website',
                utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                utmContent: 'banner-cineamo_app'
            })}
            onClick={() =>
                track('App Banner Clicked', {
                    page: currentPageName,
                    device: deviceOs
                })
            }>
            <div className="flex flex-row space-x-10">
                <IconAppStore24 fill="#FFFFFF" />
                <IconGooglePlayColored24 />
            </div>
            <span className="text-center">
                <AriaHiddenEmoji text={bannerText} />
            </span>
        </a>
    );
}
